import { createContext, useContext } from "react";
import { Cancel, CheckCircle, Info, SvgIconComponent, Warning } from "@mui/icons-material";
import {
  Box,
  BoxProps,
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  DialogTitleProps
} from "@mui/material";

type Variant = "info" | "success" | "warning" | "error";
type AlertDialogProps = DialogProps & {
  open: boolean;
  onCancel?: () => void;
  variant?: Variant;
  hideIcon?: boolean;
};

const Icon: Record<Variant, SvgIconComponent> = {
  info: Info,
  error: Cancel,
  success: CheckCircle,
  warning: Warning
};

const alertColors: Record<Variant, { bgcolor: string; color: string }> = {
  info: {
    color: "#2F80ED", 
    bgcolor: "rgba(47, 128, 237, 0.1)" 
  },
  success: {
    color: "#12B76A",
    bgcolor: "rgba(18, 183, 106, 0.1)"
  },
  warning: {
    color: "#F79009",
    bgcolor: "rgba(247, 144, 9, 0.1)"
  },
  error: {
    color: "#F04438",
    bgcolor: "rgba(240, 68, 56, 0.1)"
  }
};

const AlertDialogContext = createContext<{
  variant: Variant;
  onCancel?: () => void;
}>({ variant: "info", onCancel: () => {} });

function AlertDialog({ PaperProps, children, variant = "info", onCancel, hideIcon, ...restProps }: AlertDialogProps) {
  return (
    <Dialog
      {...restProps}
      PaperProps={{
        ...PaperProps,
        sx: {
          borderRadius: "0.5rem",
          paddingBlock: "0.75rem",
          ...PaperProps?.sx
        }
      }}
    >
      <AlertDialogContext.Provider value={{ variant, onCancel }}>{children}</AlertDialogContext.Provider>
    </Dialog>
  );
}

function AlertTitle(props: DialogTitleProps) {
  return (
    <DialogTitle
      variant='h6'
      fontWeight={600}
      color={"grey.700"}
      {...props}
      sx={{ display: "flex", alignItems: "center", gap: "0.5rem", paddingBlock: "0.5rem", ...props.sx }}
    />
  );
}

function AlertBody(props: DialogContentProps) {
  return <DialogContent {...props} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", ...props.sx }} />;
}

function AlertAction(props: DialogActionsProps) {
  return (
    <DialogActions
      {...props}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", px: "1rem", ...props.sx }}
    />
  );
}

function AlertIcon(props: BoxProps) {
  const { variant = "info" } = useContext(AlertDialogContext);
  const DispalyIcon = Icon[variant];
  const colors = alertColors[variant];

  return (
    <Box
      {...props}
      sx={{ bgcolor: colors?.bgcolor, height: "2.5rem", width: "2.5rem", borderRadius: "50%", padding: "0.5rem", ...props.sx }}
    >
      <Box sx={{ color: colors?.color, height: "100%", width: "100%", borderRadius: "50%" }}>
        <DispalyIcon fontSize='large' />
      </Box>
    </Box>
  );
}

AlertDialog.Title = AlertTitle;
AlertDialog.Content = AlertBody;
AlertDialog.Actions = AlertAction;
AlertDialog.Icon = AlertIcon;
export default AlertDialog;
